import React, { useContext, useEffect, useMemo, useRef } from "react";
import Flatpickr from "react-flatpickr";
import { Options as FlatpickrOptions } from "flatpickr/dist/types/options";
import "flatpickr/dist/flatpickr.css";
import { KTIcon } from "../../../../_theme/helpers";
import { envConfig } from "../../../../utils/config";
import { formatToStorageDate, getDisplayDatePlaceholder, getFlatpickrDateFormat, parseDate } from "../../../../utils/date";
import { InModalContext } from "./InModalContext";

/**
 * Bootstrap modals enforce focus inside the dialog. Flatpickr's calendar is
 * appended to document.body, so month/year controls lose focus immediately.
 * Allow focus to remain on Flatpickr UI while a modal is open.
 *
 * react-bootstrap (@restart/ui Modal) enforces focus from a capture-phase `focus`
 * listener on document, so `focus` is stopped as well as `focusin`. This listener
 * is added when the module loads, before any modal opens, so it runs first.
 */
declare global {
  interface Window {
    __doiFlatpickrModalFocusFix?: boolean;
  }
}

if (typeof document !== "undefined" && !window.__doiFlatpickrModalFocusFix) {
  window.__doiFlatpickrModalFocusFix = true;
  const keepFocusOnCalendar = (e: FocusEvent) => {
    const target = e.target as HTMLElement | null;
    if (target?.closest?.(".flatpickr-calendar")) {
      e.stopImmediatePropagation();
    }
  };
  document.addEventListener("focusin", keepFocusOnCalendar, true);
  document.addEventListener("focus", keepFocusOnCalendar, true);
}

/** Parse date string as local date to avoid timezone shift (API stores "YYYY-MM-DD"). */
const parseDateAsLocal = (value: Date | string | null): Date | undefined => {
  return parseDate(value) ?? undefined;
};

interface CustomFormDateInputProps {
  value: Date | string | null;
  onChange: (date: Date | string | null) => void;
  size?: "lg" | "md" | "sm";
  className?: string;
  disabled?: boolean;
  options?: Partial<FlatpickrOptions>;
  month?: number; // 1-12
  allowedDates?: string[]; // Array of 'YYYY-MM-DD' strings
  minDate?: Date | undefined;
  maxDate?: Date | undefined;
}

const CustomFormDateInput: React.FC<CustomFormDateInputProps> = ({
  value,
  onChange,
  size = envConfig.DEFAULT_INPUT_FIELD_SIZE,
  className = `form-control form-control-${size} form-control-solid rounded-right-0`,
  disabled = false,
  minDate,
  maxDate,
  options = {},
  month,
  allowedDates,
}) => {
  const fpRef = useRef<any>(null);
  // Inside a modal the calendar renders next to the input ("static"), so it is part of the
  // dialog: the modal keeps focus in the dialog, and a calendar appended to <body> would lose
  // it, closing the month dropdown as soon as it opens.
  const inModal = useContext(InModalContext);

  const allowedDateObjects = useMemo(() => {
    if (!allowedDates) return [];

    return allowedDates
      .map((d) => parseDate(d))
      .filter((dt): dt is Date => dt !== null);
  }, [allowedDates]);

  useEffect(() => {
    if (!fpRef.current) return;

    const today = new Date();

    // Month restriction
    if (month) {
      const firstOfMonth = new Date(today.getFullYear(), month - 1, 1);
      const lastOfMonth = new Date(today.getFullYear(), month, 0);
      fpRef.current.flatpickr.set("minDate", firstOfMonth);
      fpRef.current.flatpickr.set("maxDate", lastOfMonth);
      fpRef.current.flatpickr.jumpToDate(firstOfMonth);
    }
    if (allowedDateObjects.length > 0) {
      const filteredDates = month
        ? allowedDateObjects.filter(
          (d) => d.getMonth() + 1 === month
        )
        : allowedDateObjects;

      fpRef.current.flatpickr.set("enable", filteredDates);

      // If current value is invalid, reset
      const parsedValue = parseDateAsLocal(value);
      if (
        parsedValue &&
        !filteredDates.some(
          (d) => d.toDateString() === parsedValue.toDateString()
        )
      ) {
        onChange(null);
      }
    }
  }, [month, allowedDateObjects, value]);


  return (
    <div
      className="input-group flex-nowrap"
      style={{ cursor: disabled ? "not-allowed" : "pointer" }}
    >
      <Flatpickr
        ref={fpRef}
        value={parseDateAsLocal(value) || undefined}
        options={{ dateFormat: getFlatpickrDateFormat(), minDate, maxDate, static: inModal, ...options }}
        onChange={(selectedDates) => {
          const selected = selectedDates[0] || null;

          if (!selected) {
            onChange(null);
            return;
          }

          onChange(formatToStorageDate(selected));
        }}
        className={className}
        placeholder={getDisplayDatePlaceholder()}
        disabled={disabled}
      />

      <span
        className="input-group-text position-relative"
        style={{ cursor: disabled ? "not-allowed" : "pointer" }}
        onClick={() => !disabled && fpRef.current?.flatpickr.open()}
      >
        <KTIcon
          className={size === "lg" ? "fs-1" : size === "md" ? "fs-3" : "fs-4"}
          iconName="calendar"
        />
      </span>
    </div>
  );
};

export default CustomFormDateInput;
